import React from 'react'
import { EmailBlockContent } from './EmailBlockContent'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from './EmailBlock.module.scss'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { shouldShowEmailBlockWrapper, type EmailChannel } from './constants'
import { useMailchimpIntegration } from './hooks/useMailchimpIntegration'
import { EmailPublishingParams, useEmailPublishing } from './hooks/useEmailPublishing'
import { useEmailSubjectLine } from './hooks/useEmailSubjectLine'
import { useEmailAudience } from './hooks/useEmailAudience'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { EmailPostSettings } from 'api/clients/ScheduledPostsClient'

export const EmailBlockWrapperV2 = ({
  workspaceId,
  scheduledPostId,
  channel,
}: {
  workspaceId: number
  scheduledPostId: number
  channel: EmailChannel | null
}) => {
  const { data: scheduledPost, isLoading: scheduledPostLoading } = useScheduledPost({
    id: scheduledPostId,
  })
  const updateScheduledPost = useUpdateScheduledPost({ suppressToast: true })

  const {
    mailchimpProfile,
    audienceLists,
    audienceLoading,
    audienceError,
    integrationsLoading,
    hasIntegration,
    hasProfile,
  } = useMailchimpIntegration(workspaceId)

  const { postAt, status, updatePostAt, clearPostAt, updateStatus } = usePostProperties({
    id: scheduledPost?.source_id || '',
    type: AssigneeType.Article,
  })

  const { handleEmailPublish, isPublishing } = useEmailPublishing(scheduledPost)

  const { subjectLine, handleUpdateSubjectLine } = useEmailSubjectLine(scheduledPost)
  const { listId, handleUpdateAudience } = useEmailAudience(scheduledPost)

  // Check if post has already been successfully published
  // A post is considered published when: not posting, didn't fail, and has a post URL
  const isPostAlreadyPublished =
    scheduledPost && !scheduledPost.is_posting && !scheduledPost.posting_failed && !!scheduledPost.post_url

  // V2 uses standard publishing flow - "Open in Mailchimp" functionality removed
  // The backend handles campaign creation through the /publish endpoint
  const handleOpenMailchimp = () => {
    // TODO: Consider implementing this in a future ticket if users request it
    // console.log('V2: Open in Mailchimp functionality not available with standard publishing flow')
  }

  if (!shouldShowEmailBlockWrapper(channel) || !hasIntegration || !hasProfile) {
    return null
  }

  if (integrationsLoading || scheduledPostLoading) {
    return (
      <div className={Styles.spinnerContainer}>
        <Spinner />
      </div>
    )
  }

  const handleUpdateScheduledPost = async (params: EmailPublishingParams) => {
    if (!scheduledPost) return
    const currentSettings = (scheduledPost.post_settings as EmailPostSettings) || {}

    await updateScheduledPost.mutateAsync({
      id: scheduledPost.id,
      params: {
        postSettings: {
          ...currentSettings,
          list_id: params.listId,
          subject_line: params.subject,
          from_name: params.fromName,
          reply_to: params.replyTo,
        },
      },
    })
  }

  const handleUpdatePostAt = async (time: string | null, params: EmailPublishingParams) => {
    if (time === null) {
      await Promise.all([clearPostAt(), updateStatus(PostStatusPropertyValue.Draft)])
      return
    }
    await Promise.all([
      handleUpdateScheduledPost(params),
      updatePostAt(time),
      updateStatus(PostStatusPropertyValue.AutoPostScheduled),
    ])
    showToastMessage('Send time updated!', 'success')
  }

  return (
    <EmailBlockContent
      audienceLoading={audienceLoading}
      audienceError={audienceError}
      audienceLists={audienceLists}
      mailchimpProfile={mailchimpProfile}
      handleOpenMailchimp={handleOpenMailchimp}
      handleSendNow={handleEmailPublish}
      sendLoading={isPublishing}
      openLoading={false} // V2 doesn't support "Open in Mailchimp"
      subjectLine={subjectLine}
      scheduledPostLoading={scheduledPostLoading}
      handleUpdateSubjectLine={handleUpdateSubjectLine}
      listId={listId}
      handleUpdateAudience={handleUpdateAudience}
      isPostAlreadyPublished={isPostAlreadyPublished} // Disable send button for published posts
      postAt={postAt}
      status={status?.value}
      onScheduledTimeChange={handleUpdatePostAt}
      scheduledPostId={scheduledPostId}
      articleId={scheduledPost?.source_id || ''}
    />
  )
}
